import { useAuthContext } from "@features/auth/contexts/AuthContext";
import Landing from "@features/auth/screens/Landing";
import { AppBar } from "@features/dashboard/layouts/AppBar";
import { useAppStore } from "@hooks/useAppStore";
import { isWeb } from "@memorang/helpers/platform";
import Box from "@memorang/ui/components/Box";
import WelcomeFeaturesList, {
	type FeatureProp,
} from "@memorang/ui/components/WelcomeFeaturesList";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import useBreakpoints from "@memorang/ui/useBreakpoints";
import DocumentTitle from "components/DocumentTitle";
import ExternalAuthGate from "components/ExternalAuthGate";
import { Image } from "expo-image";
import {
	Redirect,
	router,
	useGlobalSearchParams,
	usePathname,
} from "expo-router";
import { useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: consider breaking into smaller components
export default function IndexRoute() {
	const { logo: appLogo, landing } = useAppStore((store) => store.app);
	const { isSignedIn } = useAuthContext();
	const {
		lgUp: isDesktopLargeUp,
		smUp: isTabletUp,
		smDown: isMobileDown,
	} = useBreakpoints();

	const theme = useAppTheme();
	const { width } = useWindowDimensions();
	const { bottom: bottomInset } = useSafeAreaInsets();
	const pathname = usePathname();
	const isRootRoute = pathname === "/";
	const { auth_token: authToken } = useGlobalSearchParams<{
		auth_token: string;
	}>();

	const isTokenBasedFlow = useAppStore(
		(store) => store.app.auth?.tokenBasedFlow ?? false,
	);

	// Only perform redirects while on the landing screen
	if (isRootRoute) {
		if (isSignedIn && !authToken) {
			return <Redirect href="/home" />;
		}

		if (!isSignedIn && isTokenBasedFlow) {
			return <Redirect href="/auth/unauthorized" />;
		}
	}

	const handleNext = () => {
		router.push("/(auth)/login");
	};

	const isNativeTablet = isTabletUp && !isDesktopLargeUp && !isWeb;
	const showFeatureList =
		landing?.valueProps && isTabletUp && !isDesktopLargeUp && isWeb;
	const showRightPanel =
		isDesktopLargeUp && landing?.landingGraphics && landing?.valueProps;

	return (
		<ExternalAuthGate>
			<DocumentTitle title={"Welcome"} />
			{isWeb && <AppBar title="" variant="public" showLogo />}
			<Box flex={1} flexDirection="row">
				<Box
					flex={1}
					maxWidth={isDesktopLargeUp ? width / 2 : "100%"}
					// backgroundColor={theme.colors.onPrimary}
					paddingBottom={isWeb ? 0 : Math.max(24, bottomInset)}
					overflow="hidden"
					gap={24}
				>
					<Box
						paddingHorizontal={24}
						flex={
							isDesktopLargeUp || isMobileDown || isNativeTablet ? 1 : undefined
						}
						alignSelf={isWeb ? "center" : undefined}
						alignItems="center"
						justifyContent="center"
					>
						{!isWeb && (
							<Box
								flex={1}
								alignSelf="flex-end"
								width="100%"
								justifyContent="flex-end"
								alignItems="center"
								padding={24}
							>
								<Image
									source={{
										uri: theme.dark
											? appLogo.stacked.dark.uri
											: appLogo.stacked.light.uri,
									}}
									style={{ width: 275, height: 152 }}
									contentFit="contain"
								/>
							</Box>
						)}
						<Landing
							handleNext={handleNext}
							valueProps={landing?.valueProps as FeatureProp[]}
							title={
								landing?.title as {
									prefix?: string;
									decoratedText?: string;
									suffix?: string;
								}
							}
							marketText={landing?.marketText}
						/>
					</Box>

					{showFeatureList && (
						<Box
							width="100%"
							justifyContent="center"
							alignItems="center"
							paddingTop={24}
							paddingHorizontal={24}
						>
							<WelcomeFeaturesList
								valueProps={landing?.valueProps as FeatureProp[]}
							/>
						</Box>
					)}
				</Box>

				{showRightPanel && (
					<Box
						flex={1}
						justifyContent="center"
						alignItems="center"
						padding={48}
						minWidth={816}
						maxWidth={width / 2}
						gap={32}
					>
						<Image
							source={{ uri: landing?.landingGraphics ?? "" }}
							style={{
								width: "100%",
								maxWidth: 816,
								aspectRatio: 1.516,
							}}
							contentFit="contain"
						/>
						<WelcomeFeaturesList
							valueProps={landing?.valueProps as FeatureProp[]}
						/>
					</Box>
				)}
			</Box>
		</ExternalAuthGate>
	);
}
